import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import {LibraryItemCard} from 'app/components/Library/card';
import {DEFAULT_THUMBNAIL_URL, HIDDEN_LAYER_KEYS} from 'app/components/Library/LensLibraryProvider';
import {LibraryDatasetType} from 'app/components/Library/LibraryTypes';
import {LibraryItemDetailsModal} from 'app/components/Library/modal';
import {ApiSuggestedLayer} from 'app/modules/Remote/api';
import {ApiOrganization} from 'app/modules/Remote/Organization';
import {recordEvent} from 'app/tools/Analytics';
import * as CONSTANTS from 'app/utils/constants';
import {
  createLayerKeysFromLayerObject,
  getLayer,
  getLayerResolution,
  parseLayerKey,
} from 'app/utils/layerUtils';
import {orgIsAllowedTier} from 'app/utils/organizationUtils';

import cs from './styles.module.scss';

interface Props {
  organization: I.ImmutableOf<ApiOrganization>;
  suggestions: ApiSuggestedLayer[];
  loading: boolean;
  selectedLayerKeys: string[];
  setSelectedLayerKeys: (layerKeys: string[]) => void;
}

const ManagePropertiesSuggestedLayers: React.FC<Props> = ({
  organization,
  suggestions,
  loading,
  selectedLayerKeys,
  setSelectedLayerKeys,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  // Held locally rather than in the URL, the way the library does it, so opening a layer's details
  // doesn't leave query params on the upload route.
  const [detailsLayerKey, setDetailsLayerKey] = React.useState<string | null>(null);

  // Only keep what the library itself would offer as a one-click add: the model picks from the
  // org's product config, which says nothing about the tier gates and Request-a-dataset links the
  // library applies on top. Filtering before the count means the header can't promise a card the
  // row won't render, or an add the library would refuse.
  const shownSuggestions = React.useMemo(() => {
    if (!suggestions.length) {
      return suggestions;
    }
    const supportedLayerKeys = new Set(
      createLayerKeysFromLayerObject(
        organization.get('supportedLayersBySource').toJS() as Record<string, string[]>
      )
    );
    return suggestions.filter(({layerKey}) => {
      if (!supportedLayerKeys.has(layerKey) || HIDDEN_LAYER_KEYS.includes(layerKey)) {
        return false;
      }
      const {allowedTiers, libraryAction} = getLayer(layerKey, true);
      return !libraryAction && (!allowedTiers || orgIsAllowedTier(organization, allowedTiers));
    });
  }, [organization, suggestions]);

  // A fresh set opens the row; a close the user made stands until the next set. Keyed on the keys
  // themselves rather than the array, which is a new object on every parent render.
  const shownKeys = shownSuggestions.map(({layerKey}) => layerKey).join();
  React.useEffect(() => {
    if (shownKeys) {
      setIsOpen(true);
    }
  }, [shownKeys]);

  if (!loading && !shownSuggestions.length) {
    return null;
  }

  const unaddedKeys = shownSuggestions
    .map(({layerKey}) => layerKey)
    .filter((layerKey) => !selectedLayerKeys.includes(layerKey));

  const removeLayer = (layerKey: string) => {
    recordEvent('Removed dataset', {
      name: getLayer(layerKey, true).shortName,
      libraryKey: layerKey,
      portfolioCount: 1,
    });
    setSelectedLayerKeys(selectedLayerKeys.filter((key) => key !== layerKey));
  };

  // One event per layer, so adding the whole row and adding each card separately count the same.
  const addLayers = (layerKeys: string[]) => {
    layerKeys.forEach((layerKey) =>
      recordEvent('Added dataset', {
        name: getLayer(layerKey, true).shortName,
        libraryKey: layerKey,
        portfolioCount: 1,
      })
    );
    setSelectedLayerKeys([...selectedLayerKeys, ...layerKeys]);
  };

  const sourceLabel = (layerKey: string) => {
    const details = CONSTANTS.SOURCE_DETAILS_BY_ID[parseLayerKey(layerKey).sourceId];
    return details ? `${details.operator} ${details.name}`.trim() : '';
  };

  // Shared by the card and the details modal so the two can't disagree about what a click does.
  const addRemoveButton = (layerKey: string) => {
    const added = selectedLayerKeys.includes(layerKey);
    return (
      <B.Button
        icon={added ? 'cross' : 'plus'}
        text={added ? 'Remove from portfolio' : 'Add to portfolio'}
        onClick={(event) => {
          added ? removeLayer(layerKey) : addLayers([layerKey]);
          // The card behind the button opens the details modal, which a add/remove shouldn't do.
          event.stopPropagation();
        }}
      />
    );
  };

  const detailsLayer = detailsLayerKey ? getLayer(detailsLayerKey, true) : null;

  return (
    <div className={cs.suggestedLayers}>
      <div className={cs.suggestedLayersHeader}>
        <B.Button
          minimal
          alignText="left"
          icon={isOpen ? 'chevron-down' : 'chevron-right'}
          rightIcon={loading ? <B.Spinner size={14} /> : undefined}
          disabled={loading}
          onClick={() => setIsOpen((open) => !open)}
          text={
            loading ? 'Finding suggested layers…' : `Suggested layers · ${shownSuggestions.length}`
          }
        />
        {!loading && (
          <B.Button
            minimal
            icon="plus"
            text="Add all"
            disabled={!unaddedKeys.length}
            onClick={() => addLayers(unaddedKeys)}
          />
        )}
      </div>
      <B.Collapse isOpen={isOpen}>
        <div className={cs.suggestedLayersRow}>
          {shownSuggestions.map(({layerKey, reason}) => {
            const layer = getLayer(layerKey, true);

            return (
              <div key={layerKey} className={cs.suggestedLayer}>
                <LibraryItemCard
                  heading={layer.shortName}
                  subheading={sourceLabel(layerKey)}
                  resolution={
                    <B.Tag minimal round>
                      {`${getLayerResolution(layerKey)}m`}
                    </B.Tag>
                  }
                  type={LibraryDatasetType.LAYER}
                  thumbnailUrl={layer.thumbnailUrl || DEFAULT_THUMBNAIL_URL}
                  noDetailView
                  layerKey={layerKey}
                  setDetailsModalOpen={setDetailsLayerKey}
                  actionButton={addRemoveButton(layerKey)}
                />
                <div className={cs.suggestedLayerReason}>{reason}</div>
              </div>
            );
          })}
        </div>
      </B.Collapse>
      {detailsLayer && detailsLayerKey && (
        <LibraryItemDetailsModal
          isOpen
          onClose={() => setDetailsLayerKey(null)}
          actionButton={addRemoveButton(detailsLayerKey)}
          name={detailsLayer.shortName}
          source={sourceLabel(detailsLayerKey)}
          description={detailsLayer.description || ''}
          license={detailsLayer.license}
          tags={detailsLayer.tags}
          resolution={getLayerResolution(detailsLayerKey)}
          geographicExtent={detailsLayer.geographicExtent}
          thumbnailUrl={detailsLayer.thumbnailUrl || DEFAULT_THUMBNAIL_URL}
          type={LibraryDatasetType.LAYER}
        />
      )}
    </div>
  );
};

export default ManagePropertiesSuggestedLayers;
